import React from 'react';
import { Link } from 'react-router-dom';
import { Button, T } from '../components/UI';
import { HeroCarIllustration } from '../components/CarCard';

const MILESTONES = [
  { year: '2014', e: 'Founded in Dublin — first cars imported from the UK' },
  { year: '2016', e: 'Moved to dedicated forecourt premises' },
  { year: '2018', e: 'Launched bespoke custom import service' },
  { year: '2020', e: 'Expanded to larger Dublin forecourt' },
  { year: '2022', e: 'Surpassed 1,000 cars imported and sold' },
  { year: '2024', e: 'Launched online platform for browsing stock' },
];

const TEAM = [
  { name: 'Michael Brennan', role: 'Founder & Director', bio: 'Over 15 years in the automotive industry. Michael started Northbridge after seeing how difficult it was for Irish buyers to access quality UK stock at fair prices.', init: 'MB' },
  { name: 'Sinéad Kelly',    role: 'Head of Sourcing',   bio: 'Sinéad manages our UK sourcing network — main dealers and auctions — ensuring every car we acquire meets our strict quality standards.', init: 'SK' },
  { name: 'Tomás O\'Brien',  role: 'Import & Logistics', bio: 'Tomás handles all transport, VRT and registration logistics, making sure your car arrives in Ireland smoothly and legally registered.', init: 'TO' },
];

const VALUES = [
  { h: 'Honesty first',      b: 'Full cost breakdown upfront — purchase price, VRT, transport. No surprises at collection.' },
  { h: 'Thorough checks',    b: 'Full Cartell history on every car. We won\'t touch a vehicle with a questionable past.' },
  { h: 'Quality over volume',b: 'We\'d rather sell ten cars we\'re proud of than a hundred we\'re not. Every car on our forecourt has passed our standard.' },
  { h: 'Always reachable',   b: 'We\'re a small team and we answer the phone. You\'ll always speak to someone who knows your file.' },
];

export default function About() {
  return (
    <div style={{ paddingTop: 68 }}>

      {/* Hero — full-bleed split */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '65svh' }}>
        <div style={{ background: T.ink, padding: 'clamp(48px,8vw,100px) clamp(2rem,5vw,5rem)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>Our story</p>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.5rem,5vw,4.5rem)', fontWeight: 400, color: T.bg, lineHeight: 1.08, letterSpacing: '-0.02em', maxWidth: '16ch' }}>
            Built on trust,<br/><em style={{ color: T.accent }}>driven by results.</em>
          </h1>
        </div>
        <div style={{ position: 'relative', overflow: 'hidden', background: T.surface }}>
          <img src="https://picsum.photos/id/699/1000/900" alt="Rolling green hills — Ireland landscape atmosphere" loading="lazy" width="1000" height="900"
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.72) saturate(0.85)', display: 'block' }} />
          <p style={{ position: 'absolute', bottom: 12, right: 14, fontSize: 10, color: 'rgba(232,230,225,0.4)', fontStyle: 'italic' }}>Photo: Biegun Wschodni — Unsplash</p>
        </div>
      </div>

      {/* Story + timeline */}
      <div style={{ paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(40px,8vw,120px)', alignItems: 'start' }}>
          <div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>Who we are</p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 28 }}>
              Ireland's trusted UK import specialists
            </h2>
            <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.8, marginBottom: 18 }}>
              We started Northbridge because we saw a gap. Irish buyers were paying over the odds for cars freely available in the UK at significantly lower prices — yet the import process seemed complex and risky.
            </p>
            <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.8, marginBottom: 18 }}>
              We built a process that removes all the complexity. Our team handles sourcing, purchase, transport, VRT, NCT preparation and registration. By the time you get the keys, everything is done.
            </p>
            <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.8, marginBottom: 36 }}>
              Today we've helped over 1,000 customers across Ireland get into the car they wanted, at a price that made sense.
            </p>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, borderTop: `1px solid ${T.surface}`, borderLeft: `1px solid ${T.surface}` }}>
              {[['1,000+','Cars imported'],['10+','Years trading'],['4.9 ★','Customer rating']].map(([v,l]) => (
                <div key={l} style={{ padding: '20px 18px', borderRight: `1px solid ${T.surface}`, borderBottom: `1px solid ${T.surface}` }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem,3vw,2.5rem)', fontWeight: 400, color: T.accent, lineHeight: 1 }}>{v}</div>
                  <div style={{ fontSize: 'var(--step-xs)', color: T.muted, marginTop: 4, letterSpacing: '0.04em' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 24 }}>Our journey</p>
            <div>
              {MILESTONES.map((m, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 20, paddingBlock: 20, borderTop: `1px solid ${T.surface}` }}>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-sm)', fontWeight: 400, color: T.accent, paddingTop: 2 }}>{m.year}</span>
                  <span style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.6 }}>{m.e}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Car illustration band */}
      <div style={{ background: T.surface, paddingBlock: 'clamp(40px,5vw,64px)', paddingInline: 'var(--margin)', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
            {[{b:'SUV',c:'Black'},{b:'Saloon',c:'Silver'},{b:'Estate',c:'Blue'},{b:'Coupe',c:'Red'}].map((car, i) => (
              <div key={i} style={{ background: T.bg, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <HeroCarIllustration bodyType={car.b} colour={car.c} />
                <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{car.b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team */}
      <div style={{ paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>The team</p>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: 48 }}>Meet the people behind Northbridge</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {TEAM.map((member, i) => (
              <div key={i} style={{ background: T.surface, padding: '36px 32px' }}>
                <div style={{ width: 56, height: 56, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, color: T.bg, fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 'var(--step-body)' }}>
                  {member.init}
                </div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', fontWeight: 400, color: T.ink, marginBottom: 4, letterSpacing: '-0.01em' }}>{member.name}</h3>
                <p style={{ fontSize: 'var(--step-xs)', color: T.accent, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>{member.role}</p>
                <p style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.7 }}>{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values */}
      <div style={{ background: T.surface, paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>What we stand for</p>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: 48 }}>Our values</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {VALUES.map((v, i) => (
              <div key={i} style={{ background: T.bg, padding: '32px 32px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 28, alignItems: 'start' }}>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', fontWeight: 400, color: T.ink, lineHeight: 1.2, letterSpacing: '-0.01em' }}>{v.h}</h3>
                <p style={{ fontSize: 'var(--step-sm)', color: T.muted, lineHeight: 1.7, paddingTop: 4 }}>{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: T.ink, paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 28 }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,3.5rem)', fontWeight: 400, color: T.bg, lineHeight: 1.1, letterSpacing: '-0.02em', maxWidth: '20ch' }}>
            Ready to work with us?
          </h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/stock"><Button variant="primary" size="lg">Browse stock</Button></Link>
            <Link to="/import"><Button variant="outlineGold" size="lg">Import a car</Button></Link>
            <Link to="/contact"><Button variant="light" size="lg">Contact us</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
